import React, {useState} from 'react';
import {Row} from './styles';
import {useNavigate} from 'react-router-dom';
import {NestedKeys} from 'shared/types/key-manipulation';
import Column from '../column';
import {ProjectionOptionProps} from '../..';
import getNestedKey from 'shared/util/get-nested-key';
import {
  translatedProps,
  TranslationKeyProps,
} from 'shared/util/translated-props';

interface TableRowProps<T> {
  rowIndex: number;
  onRowClick?: (data: any) => void;
  data: any;
  translatedPropsKey: keyof typeof translatedProps | TranslationKeyProps;
  reorderRows: (from: string, to: string) => void;
  draggable?: boolean;
  options: (NestedKeys<T> | ProjectionOptionProps<T>)[];
}

const TableRow: <T>(p: TableRowProps<T>) => React.ReactElement<T> = ({
  onRowClick,
  translatedPropsKey,
  data,
  options,
  reorderRows,
  draggable,
  rowIndex,
}) => {
  const navigation = useNavigate();
  const [isDragOver, setDragOver] = useState(false);

  const getHeaderLabel = (option: NestedKeys | ProjectionOptionProps) => {
    if (typeof option === 'string')
      return getNestedKey(
        typeof translatedPropsKey === 'string'
          ? translatedProps[translatedPropsKey]
          : translatedPropsKey,
        option,
      );
    if (option.label) return option.label;
    return getNestedKey(
      typeof translatedPropsKey === 'string'
        ? translatedProps[translatedPropsKey]
        : translatedPropsKey,
      (option as any).value,
    );
  };

  const getColumnFromOption = (option: ProjectionOptionProps | string) =>
    typeof option === 'string'
      ? option
      : option.column
      ? option.column
      : option.value!;

  return (
    <Row
      {...{isDragOver, draggable}}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDrop={(e) => {
        reorderRows(e.dataTransfer.getData('field'), data.id);
        setDragOver(false);
      }}
      onDragLeave={() => setDragOver(false)}
      onDragStart={(e) => e.dataTransfer.setData('field', data.id)}
      onClick={() =>
        onRowClick ? onRowClick(data) : navigation((data as any).id)
      }>
      {options.map((option, index) => (
        <Column
          key={index}
          dataLabel={getHeaderLabel(option)}
          {...{column: getColumnFromOption(option), row: data, rowIndex}}
        />
      ))}
    </Row>
  );
};

export default TableRow;
